import * as React from "react";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FeatureCardSimple } from "@/components/ui/feature-card-simple";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import { SEO } from "@/components/SEO";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import PDFDownloadButton from "@/components/ui/pdf-download-button";
import { useOptimizedObserver } from "@/hooks/useOptimizedObserver";
import aureumLogo from "@/assets/aureum-logo-new.png";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { RelatedProducts } from "@/components/RelatedProducts";
import { Footer } from "@/components/Footer";
import { ContactForm } from "@/components/ui/contact-form";
import SocialShare from "@/components/blog/SocialShare";
import { ShippingTimeInfo } from "@/components/ShippingTimeInfo";
import { 
  Droplets, Waves, Lightbulb, Thermometer, Volume2, 
  Sparkles, CheckCircle, Shield, Award, Zap, Star,
  ArrowRight, Download, Play
} from "lucide-react";

// Import VENA series images
import { ProminentCTA } from "@/components/ui/prominent-cta";
import venaSeriesImage from "@/assets/vena-series-product.jpg";

import { PriceFrom } from "@/components/products/PriceFrom";
const Vena = () => {
  useOptimizedObserver();

  const specifications = [
    { label: "Auto Lid Opening", value: "Motion Sensor Activated" },
    { label: "Night Illumination", value: "LED Ambient Lighting" },
    { label: "Cleansing System", value: "Gentle Water Flow Technology" },
    { label: "Water Temperature", value: "Adjustable Heating System" },
    { label: "Eco Mode", value: "Water & Energy Saving Features" },
    { label: "Installation", value: "Wall-Mount & Floor-Mount Options" },
    { label: "Warranty", value: "3 Years Premium Coverage" },
    { label: "Certification", value: "CE Certified, ISO Compliant" }
  ];

  const features = [
    {
      icon: <Droplets className="w-8 h-8" />,
      title: "Smart Water System",
      description: "Intelligent water flow with temperature control and eco-saving technology"
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: "Night Illumination",
      description: "Soft LED lighting guides you safely in darkness without harsh brightness"
    },
    {
      icon: <Waves className="w-8 h-8" />,
      title: "Motion Detection",
      description: "Automatic lid opening and closing with precision motion sensors"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Hygiene Protection",
      description: "Advanced antibacterial coating and self-cleaning technology"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="VENA M80F Eco-Luxury Smart Toilet | Water-Saving Bidet"
        description="VENA M80F eco-luxury smart toilet: motion-sensor automation, water-saving dual flush, LED night light and antibacterial self-cleaning. CE certified."
        keywords="eco-luxury smart toilet, VENA M80F sustainable, eco-friendly bidet system, motion sensor smart toilet, water-saving luxury toilet, sustainable bathroom technology, premium eco toilet, intelligent green sanitary system, LED night light toilet, antibacterial coating, dual-flush toilet, energy-efficient bidet, WaterSense toilet, green bathroom fixtures, auto flush motion sensor, ambient lighting toilet, water saving toilet technology, bidet functionality, self-cleaning nozzle system, eco-friendly smart toilets Marbella, sustainable bathroom Marbella Spain, water-saving toilets Costa del Sol, green luxury bathrooms Andalusia"
        url="/products/vena"
        image="https://www.aureum7trading.com/images/products/vena-series-product.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "VENA M80F Smart Toilet",
          "description": "Eco-luxury smart toilet with motion sensors, night illumination, and sustainable water technology for environmentally conscious luxury living",
          "brand": {
            "@type": "Brand",
            "name": "Aureum VII Trading"
          },
          "image": [
            "https://www.aureum7trading.com/images/products/vena-series-product.jpg"
          ],
          "gtin13": "0795479810014",
          "manufacturer": {
            "@type": "Organization",
            "name": "Aureum VII Trading"
          },
          "sku": "VENA-M80F",
          "mpn": "M80F",
          "gtin": "AVT-VEN-014",
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "priceCurrency": "EUR",
            "price": 600,
            "priceValidUntil": "2026-12-31",
            "itemCondition": "https://schema.org/NewCondition",
            "url": "https://www.aureum7trading.com/products/vena",
            "seller": {
              "@type": "Organization",
              "name": "Aureum VII Trading",
              "url": "https://www.aureum7trading.com"
            },
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingRate": {
                "@type": "MonetaryAmount",
                "value": "0",
                "currency": "EUR"
              },
              "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"]
              },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 1,
                  "maxValue": 3,
                  "unitCode": "DAY"
                },
                "transitTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 10,
                  "maxValue": 40,
                  "unitCode": "DAY"
                }
              }
            },
            "hasMerchantReturnPolicy": {
              "@type": "MerchantReturnPolicy",
              "@id": "https://www.aureum7trading.com/return-policy",
              "applicableCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"],
              "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
              "merchantReturnDays": 14,
              "returnMethod": "https://schema.org/ReturnByMail",
              "returnFees": "https://schema.org/FreeReturn",
              "itemCondition": "https://schema.org/NewCondition",
              "refundType": "https://schema.org/FullRefund"
            }
          },
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "Auto Lid Opening",
              "value": "Motion Sensor Activated"
            },
            {
              "@type": "PropertyValue",
              "name": "Eco Mode",
              "value": "Water & Energy Saving Features"
            },
            {
              "@type": "PropertyValue",
              "name": "Warranty",
              "value": "3 Years Premium Coverage"
            }
          ]
        }}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "VENA M80F", url: "/products/vena" }
        ]}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-marble">
        <div className="container mx-auto max-w-7xl">
          <BreadcrumbNav items={[
            { label: "Home", href: "/" },
            { label: "Smart Toilets", href: "/smart-toilets" },
            { label: "VENA Series" }
          ]} />
          
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="fade-in-observer">
              <div className="mb-6">
                <Badge className="bg-accent/10 text-accent-foreground border-accent/20 mb-4">
                  Eco-Luxury Collection
                </Badge>
                <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 text-primary leading-tight">
                  VENA
                  <span className="block text-2xl md:text-3xl text-muted-foreground font-medium mt-2">
                    Model M80F
                  </span>
                </h1>
                <p className="font-inter text-lg text-muted-foreground leading-relaxed mb-8">
                  The VENA M80F luxury smart toilet perfectly combines eco-conscious sophistication with premium intelligent functionality. This sustainable <Link to="/smart-toilets" className="text-primary hover:underline font-semibold">luxury smart toilet</Link> features motion sensor automation for touchless operation, gentle water-saving dual-flush cleansing systems with adjustable temperature, LED night illumination with soft ambient lighting, and advanced antibacterial self-cleaning protection. An intelligent bathroom upgrade that delivers both environmental responsibility with up to 50% water savings and accessible luxury for the modern eco-conscious homeowner. WaterSense certified and CE compliant with European quality standards. Perfect for green building certifications and sustainable home renovations. Explore our <Link to="/products/aria" className="text-primary hover:underline">wall-hung ARIA H60</Link> or compare all models in our <Link to="/blog/smart-toilet-vs-bidet" className="text-primary hover:underline font-semibold">Smart Toilet vs Bidet guide</Link>.
                </p>
              </div>
              
              <PriceFrom price={600} />

              {/* Features List */}
              <div className="mb-8 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Smart temperature controls</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Dual flushing system</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Automatic lid opening</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Easy-to-use side panel</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <PDFDownloadButton productName="VENA" />
                <Button variant="outline" asChild>
                  <a href="/smart-toilets">← Back to Collections</a>
                </Button>
              </div>
              
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
                <div className="text-center">
                  <div className="font-playfair text-2xl font-bold text-primary">3+</div>
                  <div className="font-inter text-sm text-muted-foreground">Years Warranty</div>
                </div>
                <div className="text-center">
                  <div className="font-playfair text-2xl font-bold text-primary">50%</div>
                  <div className="font-inter text-sm text-muted-foreground">Water Savings</div>
                </div>
                <div className="text-center">
                  <div className="font-playfair text-2xl font-bold text-primary">CE</div>
                  <div className="font-inter text-sm text-muted-foreground">Certified</div>
                </div>
              </div>
            </div>
            
            <div className="fade-in-observer">
              <ImageLightbox
                src={venaSeriesImage}
                alt="VENA Series smart toilet featuring sophisticated design with eco-conscious technology including motion sensors and night illumination"
                className="w-full h-96 sm:h-[500px] lg:h-[600px] object-cover rounded-lg shadow-elegant cursor-pointer hover:shadow-gold transition-all duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12 sm:mb-16 fade-in-observer">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              Eco-Conscious Innovation
            </h2>
            <p className="font-inter text-lg text-muted-foreground max-w-3xl mx-auto">
              The VENA series represents our commitment to sustainable luxury, combining environmental 
              responsibility with sophisticated smart technology features.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <article key={index} className="fade-in-observer rounded-lg border bg-card shadow-elegant hover:shadow-gold transition-all duration-500 hover:-translate-y-2 p-8 text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-accent/10 rounded-full flex items-center justify-center text-accent">
                  {feature.icon}
                </div>
                <h3 className="font-playfair text-xl font-bold text-primary">{feature.title}</h3>
                <p className="font-inter text-muted-foreground leading-relaxed">{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-marble">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16 fade-in-observer">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              Technical Excellence
            </h2>
            <p className="font-inter text-lg text-muted-foreground max-w-3xl mx-auto">
              Engineered for performance, sustainability, and luxury living with European quality standards.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <div className="fade-in-observer rounded-lg border bg-card shadow-elegant p-8 space-y-4">
              <h3 className="font-playfair text-2xl font-bold mb-6 text-primary">VENA Specifications</h3>
              {specifications.map((spec, index) => (
                <div key={index} className="flex justify-between items-center py-3 border-b border-border last:border-b-0">
                  <span className="font-inter font-medium text-muted-foreground">{spec.label}</span>
                  <span className="font-inter text-primary font-semibold text-right max-w-[60%]">{spec.value}</span>
                </div>
              ))}
            </div>

            <div className="space-y-6 fade-in-observer">
              <div className="rounded-lg border bg-card shadow-elegant p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h4 className="font-playfair text-lg font-semibold text-primary">Eco-Certified</h4>
                  <p className="font-inter text-sm text-muted-foreground">Water & Energy Star Rated</p>
                </div>
              </div>

              <div className="rounded-lg border bg-card shadow-elegant p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h4 className="font-playfair text-lg font-semibold text-primary">Premium Warranty</h4>
                  <p className="font-inter text-sm text-muted-foreground">3-Year Comprehensive Coverage</p>
                </div>
              </div>

              <div className="rounded-lg border bg-card shadow-elegant p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Award className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-playfair text-lg font-semibold text-primary">European Quality</h4>
                  <p className="font-inter text-sm text-muted-foreground">CE Certified, ISO Standards</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16 fade-in-observer">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              Sustainable Luxury Benefits
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center fade-in-observer">
              <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                <Droplets className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="font-playfair text-xl font-bold mb-4 text-primary">Water Conservation</h3>
              <p className="font-inter text-muted-foreground">
                Advanced flow technology reduces water usage by up to 50% while maintaining optimal performance.
              </p>
            </div>

            <div className="text-center fade-in-observer">
              <div className="w-20 h-20 mx-auto mb-6 bg-accent/10 rounded-full flex items-center justify-center">
                <Lightbulb className="w-10 h-10 text-accent" />
              </div>
              <h3 className="font-playfair text-xl font-bold mb-4 text-primary">Smart Illumination</h3>
              <p className="font-inter text-muted-foreground">
                Gentle night lighting provides guidance without disrupting sleep or partners.
              </p>
            </div>

            <div className="text-center fade-in-observer">
              <div className="w-20 h-20 mx-auto mb-6 bg-purple-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="font-playfair text-xl font-bold mb-4 text-primary">Effortless Hygiene</h3>
              <p className="font-inter text-muted-foreground">
                Motion-activated features and self-cleaning technology maintain optimal sanitation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping & Delivery Information */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-12 bg-white/70 backdrop-blur-sm">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <ShippingTimeInfo variant="detailed" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-elegant text-white">
        <div className="container mx-auto max-w-4xl text-center fade-in-observer">
          <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
            Experience Eco-Luxury Innovation
          </h2>
          <p className="font-inter text-lg mb-8 max-w-2xl mx-auto opacity-90">
            Transform your bathroom into an environmentally conscious sanctuary with VENA's 
            sophisticated smart technology and sustainable design.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-primary hover:bg-white/90 font-inter font-semibold px-8 py-3 shadow-lg">
                Get Premium Quote
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/smart-toilets">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 font-inter font-semibold px-8 py-3">
                View All Collections
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <RelatedProducts currentProduct="VENA" />

      {/* Prominent CTA before footer */}
      <ProminentCTA
        title="Experience Eco-Luxury Innovation"
        description="Transform your bathroom into an environmentally conscious sanctuary with VENA's sophisticated smart technology and sustainable design. Get expert guidance on eco-friendly luxury bathroom solutions."
        primaryCTA={{ text: "Get Premium Quote", href: "/contact" }}
        secondaryCTA={{ text: "Compare Eco Models", href: "/smart-toilets" }}
        showContactButtons={true}
        variant="gradient"
      />

      {/* Social Share Section */}
      <section className="py-12 px-6 lg:px-12 bg-white/70 backdrop-blur-sm container mx-auto max-w-4xl">
        <div className="text-center mb-8">
          <h2 className="font-playfair text-2xl font-medium text-primary mb-3">Share VENA M80F</h2>
          <p className="font-inter text-muted-foreground">Help others discover eco-luxury smart toilet innovation</p>
        </div>
        <SocialShare 
          url="https://www.aureum7trading.com/products/vena"
          title="VENA M80F Eco-Luxury Smart Toilet - Aureum VII Trading"
        />
      </section>

      {/* Contact Form Section */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-b from-white to-green-50/30 container mx-auto max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="font-playfair text-3xl md:text-4xl font-medium text-primary mb-4">Request VENA M80F Information</h2>
          <p className="font-inter text-lg text-muted-foreground max-w-2xl mx-auto">
            Get eco-luxury consultation, sustainable pricing, and green installation guidance for your environmentally conscious bathroom project in Marbella, Spain, or across Europe.
          </p>
        </div>
        <div className="max-w-2xl mx-auto">
          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Vena;